// HealthMonitorPanel.jsx — v119.108
// 24h visibility on the WhatsApp messaging pipeline:
//   • Inbound webhooks ingested
//   • Meta retries we ignored (idempotency)
//   • AI replies sent
//   • Outbound dedup hits (double-click protections)
// Built so the admin spots a regression of the "6 messages" bug within
// minutes instead of waiting for customer complaints.
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { zVar } from "../lib/zIndex";
import { InfoTip } from "../components/InfoTip";
import { formatKSA } from "../lib/ksaTime";

const C = {
  card: "#fff",
  border: "var(--border)",
  brown: "var(--brown-deep)",
  muted: "var(--muted)",
  good: "#16a34a",
  bad: "#dc2626",
  warn: "#d97706",
};

export default function HealthMonitorPanel() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (h = hours) => {
    setLoading(true);
    try {
      const r = await api.get(`/whatsapp/health-monitor?hours=${h}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل المراقب");
    } finally { setLoading(false); }
  }, [hours]);
  useEffect(() => { load(hours); }, [load, hours]);

  // Auto-refresh every 60s
  useEffect(() => {
    const t = setInterval(() => load(hours), 60_000);
    return () => clearInterval(t);
  }, [load, hours]);

  if (loading && !data) return <div style={{ padding: 20, color: C.muted }}>⏳ جاري التحميل...</div>;
  if (!data) return null;

  const t = data.totals || {};
  const tl = data.timeline || [];
  const g = data.wa_graph_api || {};
  const tcs = g.template_catalog_sync || {};

  return (
    <div data-testid="wa-health-monitor">
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                     marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.brown }}>
            🩺 مراقب صحة المراسلة الذكية
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>
            رؤية فورية على inbound/outbound webhooks و AI replies. يحدّث كل دقيقة.
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[6, 12, 24, 48, 72].map((h) => (
            <button key={h} type="button" onClick={() => setHours(h)}
              data-testid={`wa-health-hours-${h}`}
              style={{
                padding: "5px 12px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                background: hours === h ? "var(--brown)" : "#fff",
                color: hours === h ? "#fff" : C.brown,
                border: `1px solid ${C.border}`, cursor: "pointer",
              }}>
              {h}س
            </button>
          ))}
          <button type="button" onClick={() => load(hours)}
            data-testid="wa-health-refresh"
            style={{
              padding: "5px 12px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
              background: "#fff", color: C.brown,
              border: `1px solid ${C.border}`, cursor: "pointer",
            }}>
            🔄
          </button>
        </div>
      </div>

      {/* Verdict */}
      <div style={{
        padding: 12, borderRadius: 10, marginBottom: 12,
        background: data.healthy ? "rgba(22,163,74,0.10)" : "rgba(220,38,38,0.10)",
        borderInlineStart: `4px solid ${data.healthy ? C.good : C.bad}`,
      }}
        data-testid="wa-health-verdict">
        <div style={{ fontSize: 13, fontWeight: 800,
                       color: data.healthy ? C.good : C.bad }}>
          {data.healthy ? "✅ صحة المراسلة سليمة" : "⚠️ تنبيهات صحّية"}
        </div>
        {(data.warnings || []).map((w, i) => (
          <div key={i} style={{
            fontSize: 11.5, color: w.level === "critical" ? C.bad : C.warn,
            marginTop: 6, lineHeight: 1.7,
          }}
            data-testid={`wa-health-warn-${w.code}`}>
            {w.level === "critical" ? "🔴" : "🟡"} {w.message}
          </div>
        ))}
      </div>

      {/* KPI grid */}
      <div style={{
        display: "grid", gap: 8,
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        marginBottom: 12,
      }}>
        <Kpi label="رسائل واردة" value={t.inbound_messages || 0} testid="wa-health-kpi-inbound"
          hint="كل رسالة واتساب واردة من عميل خلال الفترة المحددة" />
        <Kpi label="مكرّرات تم تجاهلها" value={t.inbound_duplicates || 0}
          testid="wa-health-kpi-dupes"
          color={(t.inbound_duplicates || 0) > 0 ? C.warn : undefined}
          hint="Meta أعاد إرسال نفس webhook — تم تجاهله بنجاح" />
        <Kpi label="ردود الذكاء" value={t.ai_replies || 0} testid="wa-health-kpi-ai"
          hint="عدد الردود التي أرسلتها ليلى (الذكاء الاصطناعي) تلقائياً دون تدخل بشري" />
        <Kpi label="إرسال مكرّر مُحبَط" value={t.outbound_dedup_hits || 0}
          testid="wa-health-kpi-outbound-dedup"
          color={(t.outbound_dedup_hits || 0) > 0 ? C.warn : undefined}
          hint="نفس النص لنفس الرقم خلال 60ث — تم منعه" />
        <Kpi label="موجزات مرسلة" value={t.briefings_sent || 0} testid="wa-health-kpi-briefings"
          hint="عدد الموجزات الدورية المُرسلة للإدارة عبر واتساب خلال الفترة" />
        {/* جولة طارئة #80008 (2026-10-07) — عدّاد نداءات Graph API الفعلي هذه
            الساعة، مقابل حدّ الإعدادات. هذا هو العدّاد الذي كان غائبًا قبل
            الحظر — لا يمكن رصد تسارع نحو حظر جديد بدونه. */}
        <Kpi label="نداءات Graph API (هذه الساعة)"
          value={`${g.calls_this_hour ?? 0} / ${g.alert_threshold ?? "—"}`}
          testid="wa-health-kpi-graph-calls"
          color={(g.calls_this_hour || 0) >= (g.alert_threshold || Infinity) ? C.bad : undefined}
          hint="عدّاد حقيقي مباشر لكل نداء Graph فعلي (core/wa_template_catalog.py) — المصدر الوحيد المتبقي لنداءات القوالب هو المزامنة الدورية وزر «تحديث من Meta»" />
      </div>

      {/* حالة مزامنة كتالوج القوالب — SSOT بعد حادثة #80008 */}
      <div data-testid="wa-health-template-sync" style={{
        background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10,
        padding: "10px 12px", marginBottom: 12, display: "flex", flexWrap: "wrap",
        gap: 14, alignItems: "center", fontSize: 12,
      }}>
        <span style={{ fontWeight: 800, color: C.brown }}>📮 كتالوج القوالب:</span>
        <span style={{ color: tcs.stale ? C.bad : C.good, fontWeight: 700 }}>
          {tcs.stale ? "⚠ متوقف عن المزامنة (تراجع زمني)" : "✅ يُزامن بشكل طبيعي"}
        </span>
        <span style={{ color: C.muted }}>آخر مزامنة: {tcs.last_synced_at ? formatKSA(new Date(tcs.last_synced_at)) : "—"}</span>
        <span style={{ color: C.muted }}>{tcs.template_count ?? 0} قالب محفوظ</span>
      </div>


      {/* Timeline chart — recharts (v119.109) */}
      <div style={{ background: "#fff", border: `1px solid ${C.border}`,
                     borderRadius: 10, padding: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.brown, marginBottom: 8 }}>
          📊 آخر {data.window_hours} ساعة
        </div>
        <div data-testid="wa-health-timeline" style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={tl} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: C.muted }}
                interval={Math.max(0, Math.floor(tl.length / 12))} />
              <YAxis tick={{ fontSize: 10, fill: C.muted }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8,
                                  border: `1px solid ${C.border}` }}
                labelFormatter={(label, items) => {
                  const it = items?.[0]?.payload;
                  return it?.label || label;
                }}
                formatter={(value, name) => {
                  const labels = {
                    inbound: "رسائل واردة",
                    ai_replies: "ردود AI",
                    duplicates: "مكرّرات",
                  };
                  return [value, labels[name] || name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                formatter={(v) => ({ inbound: "واردة",
                                       ai_replies: "ردود AI",
                                       duplicates: "مكرّرات" }[v] || v)} />
              <Bar dataKey="inbound" fill="var(--brown)" radius={[2, 2, 0, 0]} />
              <Bar dataKey="ai_replies" fill={C.good} radius={[2, 2, 0, 0]} />
              <Line type="monotone" dataKey="duplicates" stroke={C.warn}
                strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <CustomerNamesPanel />
    </div>
  );
}

function Kpi({ label, value, hint, color, testid }) {
  return (
 <div data-testid={testid} 
      style={{ background: "#fff", border: `1px solid ${C.border}`,
                borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || C.brown,
                     lineHeight: 1.1 }}>{hint && <InfoTip testId={`${testid}-infotip`} text={hint} />}{value}</div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>{label}</div>
    </div>
  );
}

// ───── Customer Names Manager ─────
function CustomerNamesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {phone, name}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/whatsapp/customer-names?limit=30");
      setItems(r.data.items || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    try {
      await api.put(`/whatsapp/customer-names/${encodeURIComponent(editing.phone)}`,
                      { name: editing.name });
      toast.success("✅ تم تحديث الاسم");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    }
  };

  return (
    <div style={{ marginTop: 16 }} data-testid="wa-customer-names">
      <div style={{ display: "flex", justifyContent: "space-between",
                     alignItems: "center", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.brown }}>
            👥 تأكيد أسماء العملاء النشطين
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
            آخر {items.length} عملاء تواصل معهم المساعد. لو اسم خاطئ، عدّله — لن يُغيَّر تلقائياً بعدها.
          </div>
        </div>
        <button type="button" onClick={load} className="btn btn-soft"
          style={{ fontSize: 11.5 }} data-testid="wa-customer-names-refresh">🔄</button>
      </div>

      {loading ? <div style={{ color: C.muted, fontSize: 12 }}>⏳ ...</div> :
       items.length === 0 ? (
        <div style={{ padding: 18, background: "#fff", border: `1px solid ${C.border}`,
                       borderRadius: 10, textAlign: "center", color: C.muted, fontSize: 12 }}>
          لا توجد ذاكرة عملاء بعد — تظهر هنا بعد أوّل تفاعل AI.
        </div>
       ) : (
        <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10,
                       overflow: "hidden" }}>
          {items.map((c, i) => (
            <div key={c.phone} style={{
              display: "grid", gridTemplateColumns: "auto 1fr auto auto",
              gap: 8, padding: "10px 12px", alignItems: "center",
              borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : "none",
            }}
              data-testid={`wa-customer-row-${c.phone}`}>
              <div style={{ fontFamily: "monospace", fontSize: 11.5, color: C.brown,
                             direction: "ltr", minWidth: 110 }}>+{c.phone}</div>
              <div style={{ fontSize: 12.5, color: C.brown, fontWeight: 600 }}>
                {c.name || <span style={{ color: C.muted, fontWeight: 400 }}>— عام —</span>}
                {c.name_source === "admin_verified" && (
                  <span style={{ fontSize: 9.5, marginInlineStart: 6, color: C.good,
                                   background: "rgba(22,163,74,0.10)", padding: "1px 5px",
                                   borderRadius: 4, fontWeight: 700 }}>
                    ✓ مؤكّد
                  </span>
                )}
                {c.name_source === "cleared_v107" && (
                  <span style={{ fontSize: 9.5, marginInlineStart: 6, color: C.warn,
                                   background: "rgba(217,119,6,0.10)", padding: "1px 5px",
                                   borderRadius: 4, fontWeight: 700 }}>
                    ⚠ مُسح تلقائياً
                  </span>
                )}
              </div>
              <div style={{ fontSize: 10.5, color: C.muted }}>{c.wa_messages || 0} رسالة</div>
              <button type="button" onClick={() => setEditing({ phone: c.phone, name: c.name || "" })}
                data-testid={`wa-customer-edit-${c.phone}`}
                style={{ padding: "4px 10px", fontSize: 11, fontWeight: 700,
                         background: "#fff", color: C.brown, border: `1px solid ${C.border}`,
                         borderRadius: 6, cursor: "pointer" }}>
                ✏️ تعديل
              </button>
            </div>
          ))}
        </div>
       )}

      {editing && (
        <div onClick={() => setEditing(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                   zIndex: zVar('extra_9999'), display: "flex", alignItems: "center",
                   justifyContent: "center", padding: 16 }}
          data-testid="wa-customer-edit-modal">
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 12, padding: 22, maxWidth: 400,
                     width: "100%" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.brown, marginBottom: 10 }}>
              ✏️ تعديل اسم العميل
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10, direction: "ltr",
                            fontFamily: "monospace" }}>+{editing.phone}</div>
            <input value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="اسم العميل (اتركه فاضياً للترحيب العام)"
              data-testid="wa-customer-edit-input"
              style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`,
                       borderRadius: 6, fontSize: 13, marginBottom: 12,
                       fontFamily: "inherit" }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setEditing(null)}
                data-testid="wa-customer-edit-cancel"
                style={{ padding: "7px 14px", background: "#fff", color: C.brown,
                         border: `1px solid ${C.border}`, borderRadius: 6,
                         fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>إلغاء</button>
              <button type="button" onClick={save}
                data-testid="wa-customer-edit-save"
                style={{ padding: "7px 14px", background: "var(--brown)", color: "#fff",
                         border: "none", borderRadius: 6,
                         fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>💾 حفظ</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
